import { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { AutosaveIndicator } from './AutosaveIndicator'
import { useAutosaveNoteSession } from '@/hooks/use-api'
import { useToast } from '@/hooks/use-toast'
import { explainError } from '@/lib/explain-error'

interface NoteEditorProps {
  sessionId: string
  initialContent?: string | null
  onContentChange?: (content: string) => void
  /** Reports the true dirty state: true on edit, false once autosave persists it. */
  onDirtyChange?: (dirty: boolean) => void
  disabled?: boolean
}

export interface NoteEditorHandle {
  /** Persist any pending edit immediately (await before completing/closing). */
  flush: () => Promise<void>
  /** Current editor content, for atomic completion. */
  getContent: () => { notes_content: string; notes_plain_text: string }
}

export const NoteEditor = forwardRef<NoteEditorHandle, NoteEditorProps>(function NoteEditor(
  { sessionId, initialContent, onContentChange, onDirtyChange, disabled }: NoteEditorProps,
  ref
) {
  const safeInitialContent = initialContent ?? ''
  const [content, setContent] = useState(safeInitialContent)
  const [autosaveState, setAutosaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const [lastSavedAt, setLastSavedAt] = useState<string>()
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const autosaveTimeoutRef = useRef<NodeJS.Timeout>()
  const lastSavedContentRef = useRef<string>(safeInitialContent)
  // Refs so the unmount cleanup sees the latest values without re-firing on every change
  const contentRef = useRef<string>(safeInitialContent)
  const sessionIdRef = useRef<string>(sessionId)
  useEffect(() => {
    contentRef.current = content
  }, [content])
  useEffect(() => {
    sessionIdRef.current = sessionId
  }, [sessionId])

  const autosaveMutation = useAutosaveNoteSession()
  const { toast } = useToast()

  // Strip Markdown to plain text (simple implementation for MVP)
  const stripMarkdown = useCallback((markdown: string): string => {
    return markdown
      .replace(/#+\s/g, '') // Remove headers
      .replace(/\*\*/g, '') // Remove bold
      .replace(/\*/g, '') // Remove italic
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Convert links to text
      .replace(/`/g, '') // Remove code markers
      .trim()
  }, [])

  // Autosave function with debounce
  const triggerAutosave = useCallback(() => {
    if (!sessionId || !content.trim()) return

    setAutosaveState('saving')

    autosaveMutation.mutate(
      {
        id: sessionId,
        data: {
          notes_content: content,
          notes_plain_text: stripMarkdown(content),
        },
      },
      {
        onSuccess: () => {
          setAutosaveState('saved')
          setLastSavedAt(new Date().toISOString())
          lastSavedContentRef.current = content
          onDirtyChange?.(false)

          // Reset to idle after 2 seconds
          setTimeout(() => {
            setAutosaveState('idle')
          }, 2000)
        },
        onError: (error) => {
          setAutosaveState('error')
          onDirtyChange?.(true)
          toast({
            title: 'Autosave failed',
            description: explainError(error, 'Failed to save notes'),
            variant: 'destructive',
          })

          // Reset to idle after 3 seconds
          setTimeout(() => {
            setAutosaveState('idle')
          }, 3000)
        },
      }
    )
  }, [sessionId, content, autosaveMutation, stripMarkdown, toast, onDirtyChange])

  // Imperative flush + content getter for the parent (complete/close).
  useImperativeHandle(
    ref,
    () => ({
      getContent: () => ({
        notes_content: contentRef.current,
        notes_plain_text: stripMarkdown(contentRef.current),
      }),
      flush: async () => {
        if (autosaveTimeoutRef.current) clearTimeout(autosaveTimeoutRef.current)
        const latest = contentRef.current
        if (!latest.trim() || latest === lastSavedContentRef.current) return
        await autosaveMutation.mutateAsync({
          id: sessionIdRef.current,
          data: { notes_content: latest, notes_plain_text: stripMarkdown(latest) },
        })
        lastSavedContentRef.current = latest
        onDirtyChange?.(false)
      },
    }),
    [autosaveMutation, stripMarkdown, onDirtyChange]
  )

  // Handle content changes with debounced autosave
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newContent = e.target.value
    setContent(newContent)
    onContentChange?.(newContent)
    onDirtyChange?.(newContent !== lastSavedContentRef.current)

    // Clear existing timeout
    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current)
    }

    // Set new autosave timeout (1.5s debounce)
    autosaveTimeoutRef.current = setTimeout(() => {
      triggerAutosave()
    }, 1500)
  }

  // Manual save on Ctrl+S
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault()
        if (autosaveTimeoutRef.current) {
          clearTimeout(autosaveTimeoutRef.current)
        }
        triggerAutosave()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [triggerAutosave])

  // Cleanup timeout + flush pending changes on actual unmount only.
  // Empty deps + refs above ensure this fires once on unmount, not per keystroke.
  useEffect(() => {
    return () => {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current)
      }

      const latestContent = contentRef.current
      const latestSessionId = sessionIdRef.current
      if (!latestContent.trim() || latestContent === lastSavedContentRef.current) return

      // Use fetch + keepalive (sendBeacon can't set Authorization headers).
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001'
      const token = localStorage.getItem('accessToken')
      if (!token || !latestSessionId) return

      const payload = JSON.stringify({
        notes_content: latestContent,
        notes_plain_text: stripMarkdown(latestContent),
      })

      // Fire-and-forget; ignore the promise.
      void fetch(`${apiUrl}/api/v1/note-sessions/${latestSessionId}/autosave`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: payload,
        keepalive: true,
      }).catch(() => {})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Focus on mount
  useEffect(() => {
    textareaRef.current?.focus()
  }, [])

  return (
    <div className="space-y-2 flex-1 flex flex-col">
      <div className="flex items-center justify-between">
        <Label htmlFor="note-editor">Notes</Label>
        <AutosaveIndicator state={autosaveState} lastSavedAt={lastSavedAt} />
      </div>
      <Textarea
        ref={textareaRef}
        id="note-editor"
        value={content}
        onChange={handleChange}
        disabled={disabled}
        placeholder="Start typing your notes here... (Markdown supported, full rich-text editor coming in v2)"
        className="flex-1 resize-none font-mono text-sm min-h-[400px]"
        aria-label="Note content editor"
        aria-describedby="note-editor-help"
      />
      <p id="note-editor-help" className="text-xs text-muted-foreground">
        Auto-saves 1.5s after you stop typing. Press Ctrl+S / Cmd+S to save immediately.
      </p>
    </div>
  )
})
